import { useEffect, useState } from 'react';

import { isAwayNow, nextBoundary, OperatingHoursConfig } from './widgetSettings';

/**
 * IMBEE-8955 — `isAwayNow`, but it re-evaluates itself when the schedule edge passes.
 *
 * Calling `isAwayNow()` during render gives the right answer only for the render it happens in.
 * A visitor who sits on a surface across closing time keeps whatever the last render decided,
 * because nothing schedules another one. Conversation solves this for its own subtree with a
 * boundary timer; anything rendered OUTSIDE that subtree — the pre-chat form is mounted through
 * the message store — has no such subscription and goes stale.
 *
 * A timer to the next edge rather than polling: the widget can sit open for hours, and
 * re-deriving twice-a-day state every few seconds is waste. The callback re-schedules ITSELF, so
 * a second edge is still caught, and so a wake-up truncated by the 32-bit setTimeout clamp
 * recomputes and hops again rather than firing once and stopping.
 */
export const useAwayNow = (config: OperatingHoursConfig | undefined): boolean => {
    const [away, setAway] = useState(() => isAwayNow(config));

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        const schedule = () => {
            setAway(isAwayNow(config));
            const boundary = nextBoundary(config);
            if (!boundary) return;
            // setTimeout takes a 32-bit signed count of ms; anything longer fires immediately.
            const delay = Math.min(boundary.getTime() - Date.now(), 2_147_483_647);
            if (delay <= 0) return;
            timer = setTimeout(schedule, delay);
        };

        schedule();
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [config]);

    return away;
};
